/**
 * Missed calls as a queue somebody owns, not a list somebody might read.
 *
 * The failure this prevents is two dispatchers ringing the same person back within a minute
 * of each other, which reads from the customer's side as a firm that does not know what it
 * is doing. So one click takes a call, everybody else's screen learns immediately that it is
 * taken, and the server refuses the second person rather than trusting the screens to agree.
 *
 * Oldest first, because it is a queue: the person who has been waiting longest is the one
 * most likely to have already rung a competitor.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, errorKey } from '@taxi/api-client';

import { useDispatcherHub } from '../realtime/useDispatcherHub';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Chip,
  ChipGroup,
  Count,
  EmptyState,
  Input,
  ScrollList,
  Sub,
} from '../ui';
import { callsApi, type CallSummary } from './api';

/**
 * The reasons a missed call is cleared for, in the order they come up: the customer rang
 * again and was served, the number was wrong, nobody answers the call-back, they no longer
 * want a car. One click each, because the reason is required and typing it forty times a
 * shift is how a required field turns into "asdf". Anything else is typed under them.
 */
const REASONS = ['served', 'wrongNumber', 'noAnswer', 'notNeeded'] as const;

export function MissedCalls({ className }: { className?: string }) {
  const { t } = useTranslation();

  /**
   * The clock behind "three minutes ago".
   *
   * Held in state and ticked, because a relative time computed during render is both impure
   * and wrong the moment nothing else re-renders: the oldest call in this queue would still
   * claim to be three minutes old an hour later.
   */
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = globalThis.setInterval(() => setNow(Date.now()), 30_000);

    return () => globalThis.clearInterval(timer);
  }, []);


  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [waiting, setWaiting] = useState(0);

  // Open by default now that it has a card of its own rather than a strip in a column: a
  // queue behind a Show button is a queue nobody works.
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clearing, setClearing] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const refresh = useCallback(async () => {
    try {
      const queue = await callsApi.missed();

      setCalls(queue.calls);
      setWaiting(queue.waiting);
    } catch {
      // A queue that cannot be loaded is a card that shows nothing. The console still takes
      // orders, which is what matters.
    }
  }, []);

  // An async fetch rather than a call to refresh(), so that nothing sets state on the render
  // that scheduled this — the same shape every other card here uses.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const queue = await callsApi.missed();

        if (!cancelled) {
          setCalls(queue.calls);
          setWaiting(queue.waiting);
        }
      } catch {
        // As above.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useDispatcherHub({
    // The list is refetched rather than patched from the event. It is a handful of rows, the
    // server owns the ordering, and a client that maintains its own copy of a queue is a
    // client that eventually disagrees with the one the server refuses writes against.
    onMissedCallChanged: () => void refresh(),
    onReconnected: () => void refresh(),
  });

  async function act(callId: string, action: () => Promise<unknown>) {
    setBusy(callId);
    setError(null);

    try {
      await action();
      await refresh();
    } catch (failure) {
      setError(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), {
              ...failure.problem.arguments,
              defaultValue: t('missed.failed'),
            })
          : t('missed.failed'),
      );

      // Somebody else got there first, most likely. Refetching shows whose it is now.
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className={className}>
      <CardHeader
        title={t('missed.title')}
        actions={
          <Button size="sm" variant="quiet" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? t('missed.hide') : t('missed.show')}
          </Button>
        }
      >
        <Count>{waiting}</Count>
      </CardHeader>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      {/* Unlike the other two cards in this row, this one renders even when it is empty:
          "nothing to ring back" is information a dispatcher wants at a glance. */}
      {calls.length === 0 ? (
        <EmptyState>{t('missed.none')}</EmptyState>
      ) : (
        open && (
          // A firm that has been busy overnight can have a hundred of these, and this is
          // one of three cards on a row that is meant to be short.
          <ScrollList>
            {calls.map((call) => (
              <li key={call.id} className="px-3.5 py-2 border-b border-rule last:border-b-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <strong className="font-semibold tabular-nums">
                      {call.from ?? t('calls.withheld')}
                    </strong>
                    {/* Who and how long ago, not a clock time: this is a queue worked from
                        the top, and "eleven minutes" says whether it still matters where
                        "21:53" makes a dispatcher do the arithmetic. */}
                    <Sub block>
                      {call.customerId === null ? t('calls.newCaller') : t('calls.known')}
                      {' · '}
                      {ago(now - Date.parse(call.startedAt), t)}
                    </Sub>
                    {/* Its own line rather than a third item on the one above: the card is
                        narrow, and a number broken across two lines reads as two. */}
                    {call.to !== null && (
                      <Sub block className="tabular-nums">
                        {t('calls.dialledLine', { number: call.to })}
                      </Sub>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* One click. The dispatcher's own telephone rings first, then the
                        customer's, so nobody sits listening to ringing they did not start. */}
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === call.id || call.from === null}
                      onClick={() => void act(call.id, () => callsApi.dial(call.from!, call.id))}
                    >
                      {t('missed.callBack')}
                    </Button>

                    <Button
                      size="sm"
                      variant="quiet"
                      disabled={busy === call.id}
                      onClick={() => {
                        setClearing(clearing === call.id ? null : call.id);
                        setReason('');
                      }}
                    >
                      {t('missed.clear')}
                    </Button>
                  </div>
                </div>

                {clearing === call.id && (
                  <div className="flex flex-col gap-1.5 mt-1.5">
                    {/* The reason is required, and that is the point. Without one, clearing
                        is how somebody makes the list stop nagging, and a queue that is
                        emptied rather than worked is a queue nobody believes. The usual
                        reasons are one click; the unusual one is typed. */}
                    <ChipGroup label={t('missed.reason')}>
                      {REASONS.map((key) => {
                        const label = t(`missed.reasons.${key}`);

                        return (
                          <Chip
                            key={key}
                            onClick={() =>
                              void act(call.id, async () => {
                                await callsApi.clear(call.id, label);
                                setClearing(null);
                              })
                            }
                          >
                            {label}
                          </Chip>
                        );
                      })}
                    </ChipGroup>

                    <div className="flex items-center gap-1.5">
                      <Input
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t('missed.reasonPlaceholder')}
                        aria-label={t('missed.otherReason')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && reason.trim() !== '' && busy !== call.id) {
                            e.preventDefault();
                            void act(call.id, async () => {
                              await callsApi.clear(call.id, reason);
                              setClearing(null);
                            });
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={reason.trim() === '' || busy === call.id}
                        onClick={() =>
                          void act(call.id, async () => {
                            await callsApi.clear(call.id, reason);
                            setClearing(null);
                          })
                        }
                      >
                        {t('missed.confirmClear')}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ScrollList>
        )
      )}
    </Card>
  );
}

/**
 * How long ago, in a unit somebody can read.
 *
 * A queue that has been left overnight otherwise says "9513 min ago", which is a number
 * nobody converts in their head — and the whole point of the column is deciding whether a
 * call is still worth returning.
 */
function ago(millis: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  const minutes = Math.max(0, Math.round(millis / 60_000));

  if (minutes < 60) return t('missed.agoMinutes', { count: minutes });

  const hours = Math.round(minutes / 60);

  if (hours < 48) return t('missed.agoHours', { count: hours });

  return t('missed.agoDays', { count: Math.round(hours / 24) });
}
